/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                                                                           ║
 * ║                   🎮 OFFLINE MODE CONFIGURATION 🎮                        ║
 * ║                                                                           ║
 * ║  Settings for offline/practice mode gameplay. These are separate from     ║
 * ║  online game settings to allow different balancing and tuning.            ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        👤 OFFLINE PLAYER SETTINGS                         ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Offline player worm settings
 * These can differ from online play for a more casual experience
 */
export const OFFLINE_PLAYER = {
  /** Starting segment count */
  SEGMENT_COUNT: 5,
  /** Starting radius */
  RADIUS: 15,
  /** Distance between segments */
  SEGMENT_SPACING: 20,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        🤖 OFFLINE AI SETTINGS                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Offline AI worm spawning settings
 * Controls how AI opponents are generated in practice mode
 */
export const OFFLINE_AI = {
  /** Initial AI worm count */
  WORM_COUNT: 2,
  /** Target count to maintain (respawns to this) */
  TARGET_COUNT: 2,
  /** Minimum count before respawning kicks in */
  MIN_COUNT: 1,
  /** Time between respawn checks (seconds) */
  RESPAWN_INTERVAL: 3,
  /** Minimum segment count for AI worms */
  MIN_SEGMENTS: 20,
  /** Random additional segments (0 to this value) */
  RANDOM_SEGMENTS: 50,
  /** Minimum radius for AI worms */
  MIN_RADIUS: 12,
  /** Random additional radius (0 to this value) */
  RANDOM_RADIUS: 6,
  /** Segment spacing for AI worms */
  SEGMENT_SPACING: 18,
  /** Minimum spawn distance from world center */
  MIN_SPAWN_DISTANCE: 500,
  /** Random additional spawn distance */
  RANDOM_SPAWN_DISTANCE: 3500,
} as const;

/**
 * AI worm movement behavior
 * Creates natural-looking serpentine movement patterns
 */
export const AI_MOVEMENT = {
  /** Movement speed (units per second) - matches player DEFAULT_WORM_SPEED from shared constants */
  SPEED: 130,
  /** Oscillation frequency for sine wave movement */
  FREQUENCY: 0.5,
  /** Phase offset multiplier between worms (prevents sync) */
  PHASE_OFFSET: 1.5,
  /** Movement amplitude in radians */
  AMPLITUDE: Math.PI * 0.5,
  /** Angular velocity multiplier */
  ANGULAR_VELOCITY: 2,
  /** Boundary margin from world edge (turn buffer) */
  BOUNDARY_MARGIN: 100,
} as const;

/**
 * AI Brain settings for sophisticated AI behavior
 * These control the state-machine based AI decision making
 */
export const OFFLINE_AI_BRAIN = {
  /** Think interval in milliseconds (AI doesn't need frame-perfect decisions) */
  THINK_INTERVAL_MS: 50,
  /** Turn rate in radians per second (smooth turning) */
  TURN_RATE: 3.0,
  /** Boost speed multiplier when AI sprints */
  BOOST_SPEED_MULTIPLIER: 1.5,
  /** Enable debug logging for AI decisions (set to true to debug) */
  DEBUG_LOGGING: false,
  /** Enable performance profiling (logs AI computation time) */
  PERF_PROFILING: false,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        🍎 OFFLINE FOOD SETTINGS                           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Offline food spawning settings
 * Uses density-based calculation so it scales with world size
 */
export const OFFLINE_FOOD = {
  /** Spawn radius as percentage of world radius */
  SPAWN_RADIUS_PERCENT: 0.9,
  /** Food items per 1,000,000 square units (density-based) - higher = more food */
  DENSITY_PER_MILLION_SQ_UNITS: 10,
} as const;

/**
 * Offline powerup spawning settings
 * Uses density-based calculation so it scales with world size
 */
export const OFFLINE_POWERUP = {
  /** Spawn radius as percentage of world radius */
  SPAWN_RADIUS_PERCENT: 0.85,
  /** Powerups per 10,000,000 square units (density-based) - higher = more powerups */
  DENSITY_PER_10M_SQ_UNITS: 5,
} as const;

/**
 * Offline world settings (overrides shared constants for offline mode)
 */
export const OFFLINE_WORLD = {
  /** World radius for offline mode (larger than online for exploration) */
  RADIUS: 5000,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                     🏃 SPRINT MULTIPLIER SETTINGS                         ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Sprint multiplier tiers - sustained sprinting rewards higher multipliers
 * Each tier has a threshold (seconds) and a multiplier value
 */
export const SPRINT_MULTIPLIER_TIERS = [
  { threshold: 0, multiplier: 1.0, label: '1x' },
  { threshold: 5, multiplier: 1.2, label: '1.2x' },
  { threshold: 10, multiplier: 1.4, label: '1.4x' },
  { threshold: 20, multiplier: 1.6, label: '1.6x' },
  { threshold: 30, multiplier: 1.8, label: '1.8x' },
  { threshold: 60, multiplier: 2.0, label: '2x' },
  { threshold: 600, multiplier: 3.0, label: '3x' }, // 10 minutes
] as const;

/**
 * Sprint multiplier settings
 */
export const SPRINT_MULTIPLIER = {
  /** How quickly the gauge decays when not sprinting (seconds of sprint time lost per second) */
  DECAY_RATE: 2.0,
  /** Minimum sprint time before decay starts (grace period in seconds) */
  DECAY_GRACE_PERIOD: 0.5,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        🐌 SLOW CRAWL MODE SETTINGS                        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Slow crawl mode settings
 * Allows players to move slower for safe farming (hold Shift)
 * Turn rate is REDUCED because slower speed already gives tighter turns naturally
 */
export const SLOW_CRAWL = {
  /** Speed multiplier when in slow crawl mode (0.55 = slightly more than half speed) */
  SPEED_MULTIPLIER: 0.55,
  /** Turn rate multiplier when in slow crawl mode (reduced agility - slower speed already gives tighter turns) */
  TURN_RATE_MULTIPLIER: 0.8,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        📤 CONVENIENCE EXPORTS                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// Player exports
export const OFFLINE_PLAYER_SEGMENT_COUNT = OFFLINE_PLAYER.SEGMENT_COUNT;
export const OFFLINE_PLAYER_RADIUS = OFFLINE_PLAYER.RADIUS;
export const OFFLINE_PLAYER_SEGMENT_SPACING = OFFLINE_PLAYER.SEGMENT_SPACING;

// AI spawn exports
export const OFFLINE_AI_WORM_COUNT = OFFLINE_AI.WORM_COUNT;
export const OFFLINE_AI_TARGET_COUNT = OFFLINE_AI.TARGET_COUNT;
export const OFFLINE_AI_MIN_COUNT = OFFLINE_AI.MIN_COUNT;
export const OFFLINE_AI_RESPAWN_INTERVAL = OFFLINE_AI.RESPAWN_INTERVAL;
export const OFFLINE_AI_MIN_SEGMENTS = OFFLINE_AI.MIN_SEGMENTS;
export const OFFLINE_AI_RANDOM_SEGMENTS = OFFLINE_AI.RANDOM_SEGMENTS;
export const OFFLINE_AI_MIN_RADIUS = OFFLINE_AI.MIN_RADIUS;
export const OFFLINE_AI_RANDOM_RADIUS = OFFLINE_AI.RANDOM_RADIUS;
export const OFFLINE_AI_SEGMENT_SPACING = OFFLINE_AI.SEGMENT_SPACING;
export const OFFLINE_AI_MIN_SPAWN_DISTANCE = OFFLINE_AI.MIN_SPAWN_DISTANCE;
export const OFFLINE_AI_RANDOM_SPAWN_DISTANCE = OFFLINE_AI.RANDOM_SPAWN_DISTANCE;

// AI movement exports
export const AI_WORM_SPEED = AI_MOVEMENT.SPEED;
export const AI_MOVEMENT_FREQUENCY = AI_MOVEMENT.FREQUENCY;
export const AI_PHASE_OFFSET = AI_MOVEMENT.PHASE_OFFSET;
export const AI_MOVEMENT_AMPLITUDE = AI_MOVEMENT.AMPLITUDE;
export const AI_ANGULAR_VELOCITY = AI_MOVEMENT.ANGULAR_VELOCITY;
export const AI_BOUNDARY_MARGIN = AI_MOVEMENT.BOUNDARY_MARGIN;

// AI brain exports
export const AI_BRAIN_THINK_INTERVAL_MS = OFFLINE_AI_BRAIN.THINK_INTERVAL_MS;
export const AI_BRAIN_TURN_RATE = OFFLINE_AI_BRAIN.TURN_RATE;
export const AI_BRAIN_BOOST_MULTIPLIER = OFFLINE_AI_BRAIN.BOOST_SPEED_MULTIPLIER;
export const AI_BRAIN_DEBUG_LOGGING = OFFLINE_AI_BRAIN.DEBUG_LOGGING;
export const AI_BRAIN_PERF_PROFILING = OFFLINE_AI_BRAIN.PERF_PROFILING;

// Food exports
export const OFFLINE_FOOD_SPAWN_RADIUS_PERCENT = OFFLINE_FOOD.SPAWN_RADIUS_PERCENT;
export const OFFLINE_FOOD_DENSITY = OFFLINE_FOOD.DENSITY_PER_MILLION_SQ_UNITS;

// Powerup exports
export const OFFLINE_POWERUP_SPAWN_RADIUS_PERCENT = OFFLINE_POWERUP.SPAWN_RADIUS_PERCENT;
export const OFFLINE_POWERUP_DENSITY = OFFLINE_POWERUP.DENSITY_PER_10M_SQ_UNITS;

// World exports
export const OFFLINE_WORLD_RADIUS = OFFLINE_WORLD.RADIUS;

/**
 * Calculate food count based on world radius and density
 */
export function calculateFoodCount(worldRadius: number): number {
  const spawnRadius = worldRadius * OFFLINE_FOOD.SPAWN_RADIUS_PERCENT;
  const area = Math.PI * spawnRadius * spawnRadius;
  return Math.floor(area / 1_000_000 * OFFLINE_FOOD.DENSITY_PER_MILLION_SQ_UNITS);
}

/**
 * Calculate powerup count based on world radius and density
 */
export function calculatePowerupCount(worldRadius: number): number {
  const spawnRadius = worldRadius * OFFLINE_POWERUP.SPAWN_RADIUS_PERCENT;
  const area = Math.PI * spawnRadius * spawnRadius;
  return Math.floor(area / 10_000_000 * OFFLINE_POWERUP.DENSITY_PER_10M_SQ_UNITS);
}
